// Bootstrap the browser-side `window.api` shim before anything else imports it.
import './api';
import vehiclesSingleton from './vehicles_browser';

import React, { useEffect, useState  } from 'react';
import { createRoot } from 'react-dom/client';
import { ThemeProvider } from '@mui/material/styles';
import {
	Button,
	CircularProgress,
	CssBaseline,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Stack,
	Typography,
} from '@mui/material';
import theme from './theme';
import Map from './Map';
import SideBar from './SideBar';
import ParameterEditor from './ParameterEditor';
import Hud from './Hud';
import GcsMessages from './GcsMessages';
import ArmControl from './ArmControl';
import MavlinkFtpEditor from './MavlinkFtpEditor';
import Sensors from './Sensors';
import Actuators from './Actuators';
import Safety from './Safety';
import Battery from './Battery';
import Gnss from './Gnss';
import Radio from './Radio';
import SettingsPage from './SettingsPage';
import TelemetryPage from './TelemetryPage';
import MonitorPage from './MonitorPage';
import './index.css';

// Load parameter metadata XML in the background.
vehiclesSingleton.loadMetadata();

const Registered_Mavlink_Messages = ['HEARTBEAT', 'ATTITUDE', 'GPS_RAW_INT', 'STATUSTEXT', 'COMMAND_ACK', 'MAG_CAL_PROGRESS', 'MAG_CAL_REPORT', 'LOG_ENTRY', 'LOG_DATA'];
const MainAppIndex = 0;
const ParameterEditorIndex = 1;
const MavFtpIndex = 2;
const SensorsIndex = 3;
const ActuatorsIndex = 4;
const SafetyIndex = 10;
const BatteryIndex = 5;
const GnssIndex = 6;
const RadioIndex = 7;
const SettingsPageIndex = 8;
const MonitorPageIndex = 9;
const TelemetryPageIndex = 11;

window.mav = {};
window.paramList = {};
window.mavlinkSettings = {};
window.mavFtpResponse = {};

window.api.onMavlinkMessage((event, name, fields, header) => {
	if (header !== undefined && header.srcSystem !== undefined) {
		// Same srcSystem can emit HEARTBEAT from multiple components
		// (autopilot=1, telemetry radio=68, etc.). Only the autopilot's
		// heartbeat carries the real custom_mode; peripheral heartbeats
		// (autopilot=MAV_AUTOPILOT_INVALID=8, custom_mode=0) would flicker
		// the displayed flight mode if stored. Skip them.
		if (name === 'HEARTBEAT' && fields && fields.autopilot === 8) {
			return;
		}
		// Relay links may also include GCS/companion heartbeats (e.g. sysid 255).
		// They are valid MAVLink traffic but do not carry flight-mode state.
		if (name === 'HEARTBEAT' && fields && fields.type === 6) {
			return;
		}
		if (typeof window.mav[header.srcSystem] !== 'object' || window.mav[header.srcSystem] === null) {
			window.mav[header.srcSystem] = {}
			window.paramList[header.srcSystem] = {}
		}
		window.mav[header.srcSystem][name] = fields;

		if (Registered_Mavlink_Messages.includes(name)) {
			const attitudeUpdateEvent = new CustomEvent(name, {detail: {...fields, srcSystem: header.srcSystem}});
			window.dispatchEvent(attitudeUpdateEvent);
		}

		const attitudeUpdateEvent = new CustomEvent("mav", {detail: {mav: window.mav}});
		window.dispatchEvent(attitudeUpdateEvent);
	}
});

window.api.onVehiclesCleared(() => {
	window.mav = {};
	window.paramList = {};
	window.mavFtpResponse = {};
	window.dispatchEvent(new CustomEvent('vehicles-cleared', { detail: {} }));
	window.dispatchEvent(new CustomEvent('mav', { detail: { mav: window.mav } }));
	window.dispatchEvent(new CustomEvent('param-list', { detail: {} }));
});

window.api.onModeList((event, modeList) => {
	const modeListUpdateEvent = new CustomEvent('mode-list', { detail: {newModeList: modeList} });
	window.dispatchEvent(modeListUpdateEvent);
});

window.api.onMavIdList((event, mavIdList) => {
	const mavIdListUpdateEvent = new CustomEvent('mav-id-list', { detail: {newMavIdList: mavIdList} });
	window.dispatchEvent(mavIdListUpdateEvent);});

window.api.onParamList((event, mavId, paramList) => {
	window.paramList[mavId] = paramList;
	const paramListUpdateEvent = new CustomEvent('param-list', { detail: {mavId: mavId, paramList: paramList} });
	window.dispatchEvent(paramListUpdateEvent);
});

window.api.onParam((event, mavId, paramName, param) => {
	if (window.paramList[mavId] && window.paramList[mavId][paramName]) {
		window.paramList[mavId][paramName] = param;
		const paramListUpdateEvent = new CustomEvent('param-list', { detail: {} });
		window.dispatchEvent(paramListUpdateEvent);
	}
});

window.api.onParamLoaded((event, mavId, loadedParams) => {
	const paramListUpdateEvent = new CustomEvent('param-loaded', { detail: {mavId, loadedParams} });
	window.dispatchEvent(paramListUpdateEvent);
});

window.api.onParamProgress((event, mavId, received, total) => {
	const e = new CustomEvent('param-progress', { detail: { mavId, received, total } });
	window.dispatchEvent(e);
});

window.api.onMavFtpListResponse((event, mavId, listPath, listResult) => {
	if (!listPath) {
		return;
	}
	if (!window.mavFtpResponse[mavId]) {
		window.mavFtpResponse[mavId] = {};
	};
	if (listPath) {
		window.mavFtpResponse[mavId][listPath] = listResult;
		const mavFtpListResponseUpdateEvent = new CustomEvent('mav-ftp-list-response-update', { detail: {} });
		window.dispatchEvent(mavFtpListResponseUpdateEvent);
	}
});

function App() {
	const [selectedAppIndex, setSelectedAppIndex] = React.useState(MainAppIndex);
	const [selectedMavId, setSelectedMavId] = React.useState(1);
	const [mavIdList, setMavIdList] = React.useState({});
    const [modeList, setModeList] = useState({});
	const [mavTimestamp, setMavTimestamp] = useState(0);
	const [paramListTimestamp, setParamListTimestamp] = useState(0);
	const [rebootReconnectStatus, setRebootReconnectStatus] = useState(null);
	const [offlineStatus, setOfflineStatus] = useState(null);

	//Select App
    const handleSelectApp = (index) => {
        setSelectedAppIndex(index);
    };

	//MavId
	const handleMavIdListUpdate = (event) => {
		const { newMavIdList } = event.detail;
		if (newMavIdList !== undefined) {
			setMavIdList(newMavIdList);
			const id_keys = Object.keys(newMavIdList);
			if (id_keys.length > 0) {
				// Auto-select the first available SYS ID when the current
				// selection is missing from the list (e.g. real vehicle uses
				// a sysid other than the default 1). Use functional setState
				// because this handler closes over the initial selectedMavId
				// captured during the one-time useEffect registration.
				setSelectedMavId((prev) => {
					if (prev === null || prev === undefined) return id_keys[0];
					if (Object.prototype.hasOwnProperty.call(newMavIdList, String(prev))) return prev;
					return id_keys[0];
				});
			}
		}
	};

    const handleMavIdSelect = (newMavId) => {
        setSelectedMavId(newMavId);
    };
	//MavId End

	//Mode
	const handleModeListUpdate = (event) => {
		const { newModeList } = event.detail;
		if (newModeList !== undefined) {
			setModeList(newModeList);
		}
	};
	// Mode End

	const handleMavUpdate = (event) => {
		setMavTimestamp(Date.now());
	}

	const handleParamListUpdate = (event) => {
		setParamListTimestamp(Date.now());
	}

    useEffect(() => {
        window.addEventListener('mav-id-list', handleMavIdListUpdate);
        window.addEventListener('mode-list', handleModeListUpdate);
		window.addEventListener('param-list', handleParamListUpdate);

		//MAVLink messages
        window.addEventListener('mav', handleMavUpdate);

        return () => {
            window.removeEventListener('mav-id-list', handleMavIdListUpdate);
            window.removeEventListener('mode-list', handleModeListUpdate);
			window.removeEventListener('param-list', handleParamListUpdate);

			//MAVLink messages
        	window.removeEventListener('mav', handleMavUpdate);
        };
    }, []);

	useEffect(() => {
		let closeTimer = null;
		const handleRebootReconnectStatus = (event, status) => {
			if (!status) return;
			if (closeTimer) clearTimeout(closeTimer);
			if (status.phase === 'cancelled') {
				setRebootReconnectStatus(null);
				return;
			}
			setRebootReconnectStatus(status);
			if (status.phase === 'success') {
				closeTimer = setTimeout(() => setRebootReconnectStatus(null), 900);
			}
		};
		window.api.onRebootReconnectStatus(handleRebootReconnectStatus);
		return () => {
			if (closeTimer) clearTimeout(closeTimer);
			window.api.removeOnRebootReconnectStatus(handleRebootReconnectStatus);
		};
	}, []);

	useEffect(() => {
		let connectionHealthy = false;
		let offlineTimer = null;
		const clearOfflineTimer = () => {
			if (offlineTimer) clearTimeout(offlineTimer);
			offlineTimer = null;
		};
		const scheduleInitialOfflineCheck = () => {
			clearOfflineTimer();
			offlineTimer = setTimeout(() => {
				if (!connectionHealthy) {
					setOfflineStatus({
						success: false,
						disconnected: true,
						message: 'Connection failed: no MAVLink data received for 3 seconds. Check the baud rate.',
					});
				}
			}, 3000);
		};
		const handleOfflineStatus = (event, status) => {
			if (status?.success === false && status.disconnected) {
				clearOfflineTimer();
				if (status.manual) {
					setOfflineStatus(null);
				} else {
					setOfflineStatus(status);
				}
			} else if (status?.success === true) {
				if (status.reconnected || status.recovered) {
					clearOfflineTimer();
					setOfflineStatus(null);
				} else {
					scheduleInitialOfflineCheck();
				}
			}
		};
		const handleOfflineHealth = (event, health) => {
			connectionHealthy = Boolean(health);
			if (connectionHealthy) {
				clearOfflineTimer();
				setOfflineStatus(null);
			}
		};
		window.api.onConnectionStatus(handleOfflineStatus);
		window.api.onFlightControllerConnectionHealth(handleOfflineHealth);
		return () => {
			clearOfflineTimer();
			window.api.removeOnConnectionStatus(handleOfflineStatus);
			window.api.removeOnFlightControllerConnectionHealth(handleOfflineHealth);
		};
	}, []);

	const _renderMainApp = () => {
		return (
			<div className={`content-container ${selectedAppIndex === MainAppIndex ? 'visible' : 'hidden'}`} >
				<div className="map-background">
					<Map
						selectedMavId={selectedMavId}
						handleMavIdSelect={handleMavIdSelect}
						mavTimestamp={mavTimestamp}
						mavIdList={mavIdList}
					/>
				</div>
				<div className="hud-container">
					<Hud
						selectedMavId={selectedMavId}
						mavTimestamp={mavTimestamp}
					/>
				</div>
				<div className="gcs-messages-container">
					<GcsMessages selectedMavId={selectedMavId} />
				</div>
				<div className="arm-control-container">
					<ArmControl selectedMavId={selectedMavId} mavTimestamp={mavTimestamp} />
				</div>
			</div>
		);
	};

	const _renderParameterEditor = () => {
		return (
			<div className={`content-container ${selectedAppIndex === ParameterEditorIndex ? 'visible' : 'hidden'}`}>
				<div className="parameter-container">
					<ParameterEditor
						appIndex={ParameterEditorIndex}
						selectedAppIndex={selectedAppIndex}
						selectedMavId={selectedMavId}
						mavTimestamp={mavTimestamp}
						paramListTimestamp={paramListTimestamp}
					/>
				</div>
			</div>
		);
	};

	const _renderMavFtp = () => {
		return (
			<div className={`content-container ${selectedAppIndex === MavFtpIndex ? 'visible' : 'hidden'}`}>
				<MavlinkFtpEditor selectedMavId={selectedMavId} />
			</div>
		);
	};

	const _renderSensors = () => {
		return (
			<div className={`content-container ${selectedAppIndex === SensorsIndex ? 'visible' : 'hidden'}`}>
				<Sensors selectedMavId={selectedMavId} mavTimestamp={mavTimestamp} paramListTimestamp={paramListTimestamp} />
			</div>
		);
	};

	const _renderActuators = () => {
		return (
			<div className={`content-container ${selectedAppIndex === ActuatorsIndex ? 'visible' : 'hidden'}`}>
				<Actuators selectedMavId={selectedMavId} mavTimestamp={mavTimestamp} paramListTimestamp={paramListTimestamp} />
			</div>
		);
	};

	const _renderSafety = () => {
		return (
			<div className={`content-container ${selectedAppIndex === SafetyIndex ? 'visible' : 'hidden'}`}>
				<Safety selectedMavId={selectedMavId} paramListTimestamp={paramListTimestamp} />
			</div>
		);
	};

	const _renderBattery = () => {
		return (
			<div className={`content-container ${selectedAppIndex === BatteryIndex ? 'visible' : 'hidden'}`}>
				<Battery selectedMavId={selectedMavId} mavTimestamp={mavTimestamp} paramListTimestamp={paramListTimestamp} />
			</div>
		);
	};

	const _renderGnss = () => {
		return (
			<div className={`content-container ${selectedAppIndex === GnssIndex ? 'visible' : 'hidden'}`}>
				<Gnss selectedMavId={selectedMavId} mavTimestamp={mavTimestamp} paramListTimestamp={paramListTimestamp} />
			</div>
		);
	};

	const _renderRadio = () => {
		return (
			<div className={`content-container ${selectedAppIndex === RadioIndex ? 'visible' : 'hidden'}`}>
				<Radio selectedMavId={selectedMavId} mavTimestamp={mavTimestamp} paramListTimestamp={paramListTimestamp} modeList={modeList} />
			</div>
		);
	};

	const _renderMavlinkPage = () => {
		return (
			<div className={`content-container ${selectedAppIndex === SettingsPageIndex ? 'visible' : 'hidden'}`}>
				<SettingsPage />
			</div>
		);
	};

	const _renderMonitorPage = () => {
		return (
			<div className={`content-container ${selectedAppIndex === MonitorPageIndex ? 'visible' : 'hidden'}`}>
				<MonitorPage />
			</div>
		);
	};

	const _renderTelemetryPage = () => {
		return (
			<div className={`content-container ${selectedAppIndex === TelemetryPageIndex ? 'visible' : 'hidden'}`}>
				<TelemetryPage selectedMavId={selectedMavId} paramListTimestamp={paramListTimestamp} />
			</div>
		);
	};

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
			<div className="app-root">
				<SideBar
					selectedAppIndex={selectedAppIndex}
					handleSelectApp={handleSelectApp}
					selectedMavId={selectedMavId}
					mavTimestamp={mavTimestamp}
					mavIdList={mavIdList}
					handleMavIdSelect={handleMavIdSelect}
					modeList={modeList}
				/>
				<main className="app-main">
					{_renderMainApp()}
					{_renderParameterEditor()}
					{_renderMavFtp()}
					{_renderSensors()}
					{_renderActuators()}
					{_renderSafety()}
					{_renderBattery()}
					{_renderGnss()}
					{_renderRadio()}
					{_renderMavlinkPage()}
					{_renderMonitorPage()}
					{_renderTelemetryPage()}
				</main>
			</div>
			<Dialog
				open={Boolean(rebootReconnectStatus)}
				disableEscapeKeyDown
				onClose={() => {}}
				maxWidth="xs"
				fullWidth
			>
				<DialogTitle>Rebooting Flight Controller</DialogTitle>
				<DialogContent>
					<Stack direction="row" spacing={2} alignItems="center" sx={{ py: 1 }}>
						{rebootReconnectStatus?.phase !== 'failed' && <CircularProgress size={28} />}
						<Stack spacing={0.5}>
							<Typography>{rebootReconnectStatus?.message}</Typography>
							{rebootReconnectStatus?.phase === 'waiting' && (
								<Typography variant="body2" color="text.secondary">
									Waiting up to 5 seconds before reopening the connection.
								</Typography>
							)}
						</Stack>
					</Stack>
				</DialogContent>
				{rebootReconnectStatus?.phase === 'failed' && (
					<DialogActions>
						<Button onClick={() => setRebootReconnectStatus(null)}>Close</Button>
					</DialogActions>
				)}
			</Dialog>
			<Dialog
				open={Boolean(offlineStatus)}
				onClose={() => setOfflineStatus(null)}
				maxWidth="xs"
				fullWidth
			>
				<DialogTitle>OFFLINE</DialogTitle>
				<DialogContent dividers>
					<Typography>
						{offlineStatus?.message?.includes('Connection failed')
							? 'No MAVLink data was received after opening the connection. Check the selected baud rate.'
							: 'MAVLink communication has been lost for more than 5 seconds.'}
					</Typography>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setOfflineStatus(null)}>Close</Button>
				</DialogActions>
			</Dialog>
		</ThemeProvider>
    );
}

const container = document.getElementById('root');
const root = createRoot(container);
root.render(
    <React.StrictMode>
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <App />
        </ThemeProvider>
    </React.StrictMode>
);
